import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Editor as TinyMCEEditor } from 'tinymce';

import { SectionOutput, SectionType } from '@/api/gql/graphql';
import {
  useCreateSection,
  useGetSection,
  useGetSections,
  useRemoveQuestion,
  useUpdateSection,
} from '@/api/hooks/useModule';
import { useGetHighlights } from '@/api/hooks/useHighLights';
import { Options } from '@/components/Common/Selects/Select.types';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';
import { getSectionValidationSchema } from '@/hooks/useModuleForm';
import { SectionFormValues } from '@/hooks/useModuleForm';
import { useToast } from '@/hooks/useToast';
import { useHighlightStore } from '@/store/Highlight/HighlightStore';
import { useNavigate, useParams } from 'react-router-dom';
import { useModuleStore } from '@/store/ModuleStore/ModuleStore';
import { useLessonStore } from '@/store/LessonStore/LessonStore';

type Tab = {
  id: number;
  title: string;
  type: string;
  content: string;
  text: string;
  label: string;
};

type NewTabFormData = {
  newTabName: string;
  type: string;
};

const newTabValidationSchema = yup.object({
  newTabName: yup
    .string()
    .required('Le nom est obligatoire.')
    .max(100, 'Le nom ne peut pas dépasser 100 caractères.'),
  type: yup.string().required('Le type est obligatoire.'),
});

export const useModule = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setHighlightId, openHighlightModal } = useHighlightStore();
  const { lessonId, setLessonId } = useLessonStore();
  const { setModuleId } = useModuleStore();
  const { moduleId } = useModuleStore();
  const { mutate: createSection, isPending: isCreateSectionPending } =
    useCreateSection();
  const { data: section, isLoading: isSectionLoading } = useGetSection(
    params.lessonId ? '' : (params.moduleId as string) || (moduleId as string),
  );
  const { mutate: updateSection, isPending: isUpdateSectionPending } =
    useUpdateSection();
  const [searchHighlights, setSearchHighlights] = useState('');
  const { data: highlights } = useGetHighlights({ search: searchHighlights });

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sectionType, setSectionType] = useState<Options>();
  const { data: sectionsData } = useGetSections(lessonId);
  const [openLessonId, setOpenLessonId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);
  const [isModalConfirmationOpen, setIsModalConfirmationOpen] = useState(false);

  const { mutate: removeQuestion, isPending: isRemoveQuestionPending } =
    useRemoveQuestion();

  const [tabs, setTabs] = useState<Tab[]>([
    {
      id: 1,
      title: 'Leçon',
      type: SectionType.Main,
      content: '',
      text: '',
      label: '',
    },
  ]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    control,
    formState: { errors },
  } = useForm<SectionFormValues>({
    resolver: yupResolver(getSectionValidationSchema(tabs.length)),
    defaultValues: { title: '', position: undefined, content: [] },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    register: registerNewTab,
    handleSubmit: handleSubmitNewTab,
    setValue: setValueNewTab,
    reset: resetNewTab,
    formState: { errors: errorsNewTab },
  } = useForm<NewTabFormData>({
    resolver: yupResolver(newTabValidationSchema),
    mode: 'onSubmit',
  });

  const [editingTab, setEditingTab] = useState<Tab | null>(null);

  useEffect(() => {
    if (section) {
      setValue('title', section.title);
      setValue('position', section.position);
      setValue(
        'content',
        section.content.map((content) => content.html),
      );

      setTabs(
        section.content.map((content, index) => ({
          id: index + 1,
          title: content?.label || '',
          type: content.type,
          content: content.html,
          text: content.html,
          label: content?.label || '',
        })),
      );
    }
  }, [section, setValue]);

  // Synchronisation des onglets avec le formulaire
  useEffect(() => {
    setValue(
      'content',
      tabs.map((tab) => tab.content),
    );
  }, [tabs, setValue]);

  useEffect(() => {
    if (params.moduleId) {
      setModuleId(params.moduleId as string);
    }
  }, [params.moduleId, setModuleId]);

  const handleChangeSectionType = (values: Options) => {
    setSectionType(values);
  };

  const goToModuleUpdate = () => {
    navigate(`/lessons/${moduleId}/update-module`);
  };

  // Fonction pour préparer les données du module
  const prepareModuleData = useCallback(
    (data: SectionFormValues, isPublished?: boolean) => ({
      title: data.title,
      position: data.position,
      isPublished,
      content: tabs.map((tab) => ({
        type: tab.type as SectionType,
        text: tab.text,
        html: tab.content,
        label: tab.title,
      })),
    }),
    [tabs],
  );

  // Fonction pour gérer les succès
  const handleSuccess = useCallback(
    (message: string) => {
      showToast(message, 'success', { autoClose: 5000 });
      reset();
      navigate('/lessons');
    },
    [showToast, reset, navigate],
  );

  // Gestion de la sauvegarde de la section
  const handlePublish = handleSubmit((data: SectionFormValues) => {
    const moduleData = {
      ...prepareModuleData(data, true),
      lessonId: params.lessonId,
    };

    createSection(moduleData, {
      onSuccess: () => {
        handleSuccess('Section créée');
        setTabs([
          {
            id: 1,
            title: 'Leçon',
            type: SectionType.Main,
            content: '',
            text: '',
            label: '',
          },
        ]);
        setActiveTab(0);
        setIsModalConfirmationOpen(false);
        navigate('/lessons');
      },
      onError: (error) => {
        showToast(error?.message, 'error', { autoClose: 5000 });
      },
    });
  });

  const handleDraft = handleSubmit((data: SectionFormValues) => {
    const moduleData = {
      ...prepareModuleData(data, false),
      lessonId: params.lessonId,
    };

    createSection(moduleData, {
      onSuccess: () => {
        handleSuccess('Section créée');
        setTabs([
          {
            id: 1,
            title: 'Leçon',
            type: SectionType.Main,
            content: '',
            text: '',
            label: '',
          },
        ]);
        setActiveTab(0);
        navigate('/lessons');
      },
      onError: (error) => {
        showToast(error?.message, 'error', { autoClose: 5000 });
      },
    });
  });

  // Gestion de la mise à jour de la section
  const handleUpdateSection = handleSubmit((data: SectionFormValues) => {
    const moduleData = prepareModuleData(data);
    updateSection(
      { id: params.moduleId as string, input: moduleData },
      {
        onSuccess: () => {
          handleSuccess('Section modifiée');
        },
        onError: (error) => {
          showToast(error?.message, 'error', { autoClose: 5000 });
        },
      },
    );
  });

  const handleSectionUpdate = (isPublished: boolean, infoMessage: string) => {
    return handleSubmit((data: SectionFormValues) => {
      const moduleData = prepareModuleData(data, isPublished);
      updateSection(
        {
          id: (params.moduleId as string) || (moduleId as string),
          input: moduleData,
        },
        {
          onSuccess: () => {
            showToast(infoMessage, 'success', { autoClose: 5000 });
            params?.moduleId
              ? navigate('/lessons')
              : setIsModalConfirmationOpen(false);
          },

          onError: (error) => {
            showToast(error?.message, 'error', { autoClose: 5000 });
          },
        },
      );
    });
  };

  const handleHideSection = handleSectionUpdate(false, 'Section cachée');
  const handleShowSection = handleSectionUpdate(true, 'Section publiée');

  const handleEditTab = useCallback(
    (tabId: number) => {
      const tabToEdit = tabs.find((tab) => tab.id === tabId);
      if (tabToEdit) {
        setValueNewTab('type', tabToEdit.type);
        setValueNewTab('newTabName', tabToEdit.title);
        setSectionType({ _id: tabToEdit.type, name: tabToEdit.type });
        setEditingTab(tabToEdit);
        setIsModalOpen(true);
      }
    },
    [tabs, setValueNewTab, setSectionType],
  );

  const handleAddTab = handleSubmitNewTab((data) => {
    if (editingTab) {
      const updatedTabs = tabs.map((tab) =>
        tab.id === editingTab?.id
          ? {
              ...tab,
              title: data.newTabName,
              type: sectionType?._id as SectionType,
              label: data.newTabName,
            }
          : tab,
      );
      setTabs(updatedTabs);
      setEditingTab(null);
    } else {
      // Mode ajout
      const newTab = {
        id: Date.now(),
        title: data.newTabName,
        type: sectionType?._id as SectionType,
        content: '',
        text: '',
        label: data.newTabName,
      };
      setTabs([...tabs, newTab]);
      setActiveTab(tabs.length);
    }
    setIsModalOpen(false);
    setValueNewTab('newTabName', '');
    setValueNewTab('type', '');
    resetNewTab();
    setSectionType(undefined);
  });

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setValueNewTab('newTabName', '');
    setValueNewTab('type', '');
    resetNewTab();
    setSectionType(undefined);
    setEditingTab(null);
  };

  const handleGetModuleId = (moduleId: string) => {
    setIsModalConfirmationOpen(true);
    setModuleId(moduleId);
  };

  // Gestion des modifications dans l'éditeur

  const handleEditorContentChange = useCallback(
    (
      content: string,
      editor: TinyMCEEditor,
      onChange: (value: string[]) => void,
    ) => {
      const plainText = editor.getContent({ format: 'text' });
      const updatedTabs = tabs.map((tab) =>
        tab?.id === tabs[activeTab]?.id
          ? { ...tab, content, text: plainText }
          : tab,
      );
      setTabs(updatedTabs);
      setValue(
        'content',
        updatedTabs.map((tab) => tab.content),
      );
      onChange(updatedTabs.map((tab) => tab.content));
    },
    [activeTab, tabs, setValue],
  );

  // Gestion de la copie de lien
  const handleCopyLink = useCallback(
    (link: string) => {
      navigator.clipboard.writeText(`#${link}`).then(
        () => {
          showToast('Lien copié', 'success', { autoClose: 5000 });
        },
        () => {
          showToast('Erreur lors de la copie du lien', 'error', {
            autoClose: 5000,
          });
        },
      );
    },
    [showToast],
  );

  // Gestion de la modification d'un highlight
  const handleEditHighlight = useCallback(
    (id: string) => {
      setHighlightId(id);
      openHighlightModal();
    },
    [setHighlightId, openHighlightModal],
  );

  // Génération des éléments du menu
  const menuItems = useCallback(
    (id: string) => [
      createMenuItem('Copier lien', '', () => handleCopyLink(id)),
      createMenuItem('Modifier', '', () => handleEditHighlight(id)),
    ],
    [handleCopyLink, handleEditHighlight],
  );

  const menuItemsModule = useCallback(
    (section?: SectionOutput) => [
      createMenuItem('Modifier', '', goToModuleUpdate),
      createMenuItem(section?.isPublished ? 'Cacher' : 'Publier', '', () =>
        handleGetModuleId(section?._id),
      ),
      createMenuItem('Quizz', '', () =>
        navigate(`/lessons/${lessonId}/module/${section?._id}/quizz`),
      ),
      createMenuItem(
        'Supprimer',
        'font-[400] text-[#EA242C]',
        () => section?._id && handleDelete(section?._id),
      ),
    ],
    [section, navigate, lessonId],
  );

  // Gestion de la suppression d'un onglet
  const handleDeleteTab = useCallback(
    (tabId: number) => {
      if (tabs.length > 1) {
        const updatedTabs = tabs.filter((tab) => tab.id !== tabId);
        setTabs(updatedTabs);
        setActiveTab(Math.max(activeTab - 1, 0));
        setValue(
          'content',
          updatedTabs.map((tab) => tab.content),
        );
      } else {
        showToast('Impossible de supprimer le dernier onglet', 'error', {
          autoClose: 5000,
        });
      }
    },
    [tabs, activeTab, setValue, showToast],
  );

  const handleLessonClick = (id: string) => {
    setOpenLessonId(openLessonId === id ? null : id);
    setLessonId(id);
  };

  const handleDelete = (moduleId: string) => {
    setModuleToDelete(moduleId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (moduleToDelete) {
      removeQuestion(
        { id: moduleToDelete },
        {
          onSuccess: () => {
            setIsDeleteModalOpen(false);
            showToast('Module supprimé avec succès', 'success', {
              autoClose: 3000,
              position: 'top-right',
            });
          },
          onError: (error: { message: string }) => {
            showToast(error?.message, 'error', {
              autoClose: 3000,
              position: 'top-right',
            });
          },
        },
      );
    }
  };

  const handleConfirmAction = () => {
    const targetModuleId = params.moduleId || moduleId;
    if (targetModuleId) {
      if (section?.isPublished) {
        handleHideSection();
      } else {
        handleShowSection();
      }
    }
  };

  const modalTitle = section?.isPublished
    ? 'Confirmation de cachage'
    : 'Confirmation de publication';
  const modalMessage = section?.isPublished
    ? 'Valider le cachage ?'
    : 'Valider la publication ?';
  const buttonTitle =
    isUpdateSectionPending || isCreateSectionPending ? 'En cours...' : 'Oui';

  const handleValidateAndPublish = async () => {
    const isValid = await trigger(undefined, { shouldFocus: true });

    if (isValid) {
      setIsModalConfirmationOpen(true);
    }
  };

  return {
    register,
    handleSubmit,
    setValue,
    reset,
    errors,
    registerNewTab,
    handleSubmitNewTab,
    setValueNewTab,
    resetNewTab,
    errorsNewTab,
    handleChangeSectionType,
    handleEditorContentChange,
    handleAddTab,
    handlePublish,
    handleDraft,
    tabs,
    setTabs,
    activeTab,
    setActiveTab,
    isModalOpen,
    sectionType,
    handleCloseModal,
    setIsModalOpen,
    highlights,
    searchHighlights,
    setSearchHighlights,
    menuItems,
    control,
    params,
    handleUpdateSection,
    handleDeleteTab,
    handleEditTab,
    editingTab,
    isSectionLoading,
    sectionsData,
    openLessonId,
    handleLessonClick,
    handleValidateAndPublish,
    menuItemsModule,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleDelete,
    handleConfirmDelete,
    isRemoveQuestionPending,
    isModalConfirmationOpen,
    setIsModalConfirmationOpen,
    isUpdateSectionPending,
    isCreateSectionPending,
    handleHideSection,
    section,
    handleConfirmAction,
    modalTitle,
    modalMessage,
    buttonTitle,
  };
};
